import React, { useEffect, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import cn from 'classnames'
import { get, map, sumBy, isNumber, isFinite, findIndex, maxBy, isEmpty, find } from 'lodash'
import CanvasDraw from 'react-canvas-draw'
import queryString from 'query-string'
import { centsToDollars } from '../utils/formatters'
import useDeepCompareEffect from 'use-deep-compare-effect'

import Button from '../components/Button'
import { ReactComponent as CloseIcon } from '../assets/icons/richCheckout/close-button.svg'
import GratuityHeader from '../components/GratuityHeader'
import SegmentedControl from '../components/SegmentedControl'
import TipEntryModal from '../components/TipEntryModal'
import { SIGNATURE_THRESHOLD_AMOUNT } from '../constants'
import { ReactComponent as WhiteX } from '../assets/sign-here-white-x.svg'
import { ReactComponent as BlackX } from '../assets/sign-here-black-x.svg'

import BackButton from '../components/BackButton'

import NetworkIndicator from '../containers/NetworkIndicator'

import { clearCart } from '../actions/cart'
import {
  addSignatureToOrder,
  addTipToOrder,
  startTabOrderAdjustmentAndClose,
  syncOrderInitiated,
  syncOrderRequested,
  updatePaymentOnOrder,
} from '../actions/order'

import { getMenuById } from '../selectors/menus'
import { getVenueName } from '../selectors/venue'
import { makeGetOrder } from '../selectors/order'
import { selectRemoteOrderTotal } from '../selectors/orderTotalRemote'
import { getDeviceSubtype } from '../selectors/config'


import { TabOrderDetailsViewModel } from '../viewmodels/TabOrderViewModel'
import DeviceSubtypes from '../utils/deviceSubtypes'

import styles from './Signature.module.scss'
import PaymentTypes from '../utils/paymentTypes'

const makeTipSuggestion = (display, amountInCents, selected, custom) => {
  return {
    display,
    amountInCents,
    selected,
    custom,
  }
}

const makeTipEntryModalTitle = (maxGratuity) => {
  if (isFinite(maxGratuity) && maxGratuity >= 0) {
    return `${centsToDollars(maxGratuity)} maximum amount`
  } else {
    return ''
  }
}

const isUsableNumber = (value) => isNumber(value) && value > 0

const Signature = ({
  authorizationCode,
  className,
  goBack,
  isFromPayNow,
  itemCount,
  lastFour,
  maxGratuity,
  order,
  orderUuid,
  paymentId,
  tenderPayment,
  tenderAmountInCents,
  tipSuggestions,
  tipSuggestionItems,
  shouldDefaultToZeroTip,
  totalInCents,
  subtotal,
  updateOrder,
  payment,
  venueName,
  isMultiPayment,
  isCardPayment,
}) => {
  const classNames = cn(styles.signature, className)
  const signaturePadRef = useRef(null)
  const intervalRef = useRef(null)

  const [selectedTipSuggestionIndex, setSelectedTipSuggestionIndex] = useState()
  const [selectedTipAmount, setSelectedTipAmount] = useState(0)
  const [isSignatureValid, setIsSignatureValid] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [newLinesMatch, setNewLinesMatch] = useState(false)
  const [previousLines, setPreviousLines] = useState([])
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [showCustomTipModal, setShowCustomTipModal] = useState(false)
  const tipEntryModalTitle = makeTipEntryModalTitle(maxGratuity)
  
  let paymentLastFour = lastFour
  let authCode = authorizationCode

  if (isMultiPayment && tenderPayment?.paymentType !== null) {
    paymentLastFour = tenderPayment?.userAttributes?.last4
    authCode = tenderPayment?.userAttributes?.authorizationCode
  }

  let customTipModalElement = null
  useEffect(() => {
    if (!selectedTipAmount) {
    setSelectedTipAmount(0)
    }
  }, [showCustomTipModal])

  if (showCustomTipModal) {
    customTipModalElement = (
      <TipEntryModal
        key='custom'
        title={tipEntryModalTitle}
        errorMessage={tipEntryModalTitle}
        initialValue={selectedTipAmount}
        maxTipInCents={maxGratuity}
        saveButtonText="Set Tip"
        onClose={() => setShowCustomTipModal(false)}
        onSubmit={(tipInCents) => handleSetNewTip(tipInCents)}
      />
    )
  }

  const handleSetNewTip = (tipInCents) => {
    const newTipAmount = isUsableNumber(maxGratuity) ? Math.min(maxGratuity, tipInCents) : tipInCents

    setSelectedTipAmount(newTipAmount)
    triggerPopAnimation()
  }

  const triggerPopAnimation = () => {
    setShouldAnimate(true)
    setTimeout(() => setShouldAnimate(false), 100)
  }

  const selectedTipSuggestionDidChange = (selectedIndex) => {
    const selectedSuggestion = get(tipSuggestions, `[${selectedIndex}]`, {})
    const selectionIsSame = selectedIndex === selectedTipSuggestionIndex
    const resolvedTipAmount = selectionIsSame ? selectedTipAmount ?? 0 : selectedSuggestion.amountInCents ?? 0

    if (selectedSuggestion.custom) {
      setShowCustomTipModal(true)
    }

    if (!selectedSuggestion.custom && !selectionIsSame && !shouldAnimate) {
      triggerPopAnimation()
    }

    setSelectedTipSuggestionIndex(selectedIndex)
    setSelectedTipAmount(resolvedTipAmount)
  }

  const completeSignature = () => {
    if (signatureRequired) {
      const save = signaturePadRef.current.getSaveData()
      const nextDrawing = save.replace(/#FFFFFF/img, '#000000')
      signaturePadRef.current.loadSaveData(nextDrawing, true)

      setPreviousLines(JSON.parse(save).lines.length)
    }
    setIsReady(true)
  }

  const clearSignature = () => {
    signaturePadRef.current.clear()
    setIsSignatureValid(false)
  }
  
  useEffect(() => {
    let signature = ''
    if (!signatureRequired && isReady) {
      updateOrder(orderUuid, signature, selectedTipAmount, tenderPayment)
      return 
    }
    if (newLinesMatch) {
      const drawing = get(signaturePadRef, 'current.canvas.drawing')
      const signature = drawing ? drawing.toDataURL('image/svg+xml') : 'Signature Error'

      updateOrder(orderUuid, signature, selectedTipAmount, tenderPayment)
    }
    if (isReady && signatureRequired) {
      intervalRef.current = setInterval(() => {
        const newLinesDrawn = JSON.parse(signaturePadRef.current.getSaveData()).lines.length

        if (newLinesDrawn === previousLines) {
          setNewLinesMatch(true)
          clearInterval(intervalRef.current)
        }
      }, 10)
    }
  // TODO: We need to audit the exhaustive dependencies here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, newLinesMatch, previousLines])

  useDeepCompareEffect(() => {
    const initialSelectedIndex = shouldDefaultToZeroTip ? 0 : findIndex(tipSuggestions, 'selected')
    const initialSelectedTipSuggestion = get(tipSuggestions, `[${initialSelectedIndex}]`, {})
    const initialSelectedTipAmount = shouldDefaultToZeroTip ? 0 : get(initialSelectedTipSuggestion, 'amountInCents', 0)

    setSelectedTipSuggestionIndex(initialSelectedIndex)
    setSelectedTipAmount(initialSelectedTipAmount)
  }, [tipSuggestions])

  useEffect(() => () => clearInterval(intervalRef.current))
  
  const tipInCents = selectedTipAmount || 0
  const tip = centsToDollars(tipInCents)
  const tenderAmount = centsToDollars(tenderAmountInCents)
  const grandTotalInCents = paymentId ? tenderAmountInCents + tipInCents : totalInCents + tipInCents
  const signatureRequired = SIGNATURE_THRESHOLD_AMOUNT === 0 || (SIGNATURE_THRESHOLD_AMOUNT && grandTotalInCents >= SIGNATURE_THRESHOLD_AMOUNT)
  const isFormValid = !signatureRequired || (isSignatureValid && isNumber(selectedTipSuggestionIndex))
 
  const grandTotal = centsToDollars(grandTotalInCents)
  const formattedOriginalTotal = centsToDollars(totalInCents)
  const clientWidth = document?.body?.clientWidth
  const clientHeight = document?.body?.clientHeight
  const canvasWidth = clientWidth < 400 ? clientWidth * 0.75 : clientWidth * .8 || 500
  const canvasHeight = () => {
    if (clientHeight < 800) {
      return clientHeight * .40
    }
    if (clientHeight <= 1000) {
      return clientHeight * .45
    }
    return clientHeight * .39
  }
  
  const allowClosingScreen = () => get(order, 'orderType') === 'preorder'

  let closeElement = null
  if (allowClosingScreen()) closeElement = <CloseIcon className={styles.closeIcon} onClick={goBack} />

  const mediaQueryMobile = window.matchMedia("(max-width: 360px)")
  const gratuityHeaderText = () => {
    if (mediaQueryMobile.matches) {
     return ( 
     <>
      {isMultiPayment ? (
        <div className={styles.textWrapper}>
          <span className={styles.orderTotal}>{`Total: ${formattedOriginalTotal}`}</span>
          <span className={styles.orderTotal}>{`Tender Amount: ${tenderAmount} ${tipInCents ? ` + Tip: ${tip}` : ''}`}</span>
          <span className={styles.orderNumber}>{`Payment: ${payment}`}</span>
        </div>
        ) : <div className={styles.textWrapper}>
              <span className={styles.orderTotal}>{`Total: ${formattedOriginalTotal} ${tipInCents ? ` + Tip: ${tip}` : ''}`}</span>
            </div>
      }
     </>
     )
   } else {
      return (
        <span className={styles.textWrapper}>
            <span className={styles.orderTotal}>{`Total: ${formattedOriginalTotal} ${isMultiPayment ? `• Tender Amount: ${tenderAmount}` : '' }${tipInCents ? ` + Tip: ${tip}` : ''} ${isMultiPayment ? `• Payment: ${payment}` : '' }`}</span>
        </span>
      )
    }
  }

  let primaryText = grandTotal
  let secondaryText = gratuityHeaderText()
  let backButtonText = mediaQueryMobile.matches ? '' : 'Go Back'

  if (isReady) {
    primaryText = 'Please Wait...'
    secondaryText = "We're attempting to proccess your transaction"
  }

  const handleSignIcon = () => {
   if (mediaQueryMobile.matches) {
      return <BlackX />
    } else {
      return <WhiteX />
    }
  }

  return (
    <div className={classNames}>
      {closeElement}
      <div className={styles.signatureNavbarWrapper}>
        <section className={styles.signatureNavbar}>
          <BackButton onClick={goBack} backButtonText={isFromPayNow ? 'Finalize Later' : backButtonText}/>
            <>
              <NetworkIndicator className={styles.indicator} />
            </>
        </section>
      </div>
      <div className={styles.gratuityHeaderWrapper}>
        <GratuityHeader
          className={cn(styles.gratuityHeader, { [styles.pop]: shouldAnimate })}
          primaryText={primaryText}
          secondaryText={secondaryText}
        />
      </div>
      <div className={styles.contentContainer}>
        <SegmentedControl items={tipSuggestionItems} selectedIndex={selectedTipSuggestionIndex} onChange={selectedTipSuggestionDidChange} />
        {(signatureRequired) && (
          <div className={styles.signaturePadContainer} onTouchEnd={() => setIsSignatureValid(true)} onMouseUp={() => setIsSignatureValid(true)}>
            <CanvasDraw
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight()}
              className={styles.signaturePad}
              brushRadius={1.5}
              brushColor="#FFFFFF"
              backgroundColor="transparent"
              catenaryColor="transparent"
              hideGrid
              ref={signaturePadRef}
            />
            <div className={styles.signHereContainer}>
              {handleSignIcon()}
              <span className={styles.signHere} onClick={clearSignature} />
            </div>
          </div>
        )}
        { isCardPayment && (
          <p className={styles.disclaimer}>I agree to pay the above total amount according to my card issuer agreement.<br/>{`Credit card ending in ${paymentLastFour}. Authorization code ${authCode}`}</p>
        )}
      </div>
      <div className={styles.buttonGroup}>
        <Button disabled={!isFormValid} onClick={completeSignature} className={styles.completeButton}>{mediaQueryMobile.matches ? "Confirm" : "DONE"}</Button>
      </div>
      {customTipModalElement}
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  const hasOrderUuid = ownProps?.match?.params?.orderUuid !== undefined
  const orderUuid = ownProps?.match?.params?.orderUuid ?? state.order.currentOrderId
  const paymentId = ownProps?.match?.params?.paymentId ?? ''
  const isFromPayNow = ownProps?.history?.location?.state?.isFromPayNow ?? false
  const getOrder = makeGetOrder()
  const order = getOrder(state, orderUuid)
  const tenderPayment = paymentId ? find(order?.paymentInfo, (payment) => payment?.uuid === paymentId) : undefined
  const orderViewModel = TabOrderDetailsViewModel(order)
  const isMultiPayment = orderViewModel.isMultiPayment
  const itemCount = sumBy(order.itemModels, 'quantity')
  const lastFour = get(order, 'cardReaderData.receiptData.maskedCardNumber', 'not available').replace(/\*/g, '')
  const authorizationCode = get(order, 'cardReaderData.authResponseData.authorizationCode', 'not available')
  const remoteOrderTotal = selectRemoteOrderTotal(state, order?.exemptionOverrides)
  const orderTotalFromOrder = order?.orderTotalFromOrder ?? {}
  const payment = orderViewModel.payment ?? ''
  const venueName = getVenueName(state)
  let orderTotal = maxBy([remoteOrderTotal, orderTotalFromOrder], 'updatedAt')

  if (hasOrderUuid && !isEmpty(orderTotalFromOrder)) {
    orderTotal = orderTotalFromOrder
  }
  
  const tenderAmountInCents = tenderPayment?.amountInCents ?? orderTotal?.subtotalInCents
  const exemptions = orderViewModel.exemptionOverrides ?? {}
  const totalInCents = orderTotal?.totalAmountInCents
  const subtotalInCents = tenderPayment?.amountInCents ?? orderTotal?.subtotalInCents
  const makeTipSuggestionFromService = (suggestion) => {
    const { tipDisplay, tipAmountInCents, tipPercentage, tipSelected, tipCustom } = suggestion
    const actualTipAmountInCents = tipAmountInCents ?? parseInt(parseFloat(tipPercentage) * subtotalInCents)

    return makeTipSuggestion(tipDisplay, actualTipAmountInCents, tipSelected, tipCustom)
  }

  let maxGratuity = Infinity
  const menu = getMenuById(state, order.standMenuUuid)
  let tipSuggestions = map(menu?.tipSuggestions, makeTipSuggestionFromService)
  let tipSuggestionItems = map(tipSuggestions, 'display')

  const subtotal = orderTotal?.subtotalDisplay ?? '$0.00'
  let shouldDefaultToZeroTip = false

  const deviceSubtype = getDeviceSubtype(state)
  if (DeviceSubtypes.isConcessions(deviceSubtype)) {
    tipSuggestions = []
    tipSuggestionItems = []
  }

  const paymentType = tenderPayment?.paymentType ?? order?.createOrderParams?.payments?.[0]?.paymentType
  const isCardPayment = PaymentTypes.isCardReader(paymentType)

  return {
    authorizationCode,
    deviceSubtype,
    isFromPayNow,
    itemCount,
    lastFour,
    maxGratuity,
    order,
    orderUuid,
    paymentId,
    shouldDefaultToZeroTip,
    tenderPayment,
    tenderAmountInCents,
    totalInCents,
    tipSuggestions,
    tipSuggestionItems,
    subtotal,
    venueName,
    payment,
    isMultiPayment,
    isCardPayment
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const paymentId = ownProps?.match?.params?.paymentId ?? ''
  const replace = ownProps?.history?.replace ?? (() => {})
  const goBack = get(ownProps, 'history.goBack', () => {})
  const queryParams = queryString.parse(get(ownProps, 'location.search', ''))
  const action = get(queryParams, 'action', '')
  const closeAction = action === 'close'
  const closeTenderAction = action === 'close-tender'
  const isFromPayNow = ownProps?.history?.location?.state?.isFromPayNow ?? false

  return {
    goBack: () => {
      if (isFromPayNow) {
        replace('/tabs')
      } else {
        goBack()
      }
    },
    updateOrder: (orderUuid, signature, tipInCents, tenderPayment) => {
      dispatch(clearCart())
      if (closeAction) {
        dispatch(addSignatureToOrder(orderUuid, signature))
        dispatch(addTipToOrder(orderUuid, tipInCents))
        dispatch(startTabOrderAdjustmentAndClose({ orderId: orderUuid, adjustmentType: 'update' }))
      } else
      if (closeTenderAction) {
        let amountInCents = tenderPayment?.amountInCents + tipInCents
        if (tenderPayment?.tip > 0) amountInCents -= tenderPayment.tip

        dispatch(updatePaymentOnOrder({ orderId: orderUuid, tip: tipInCents, signature, paymentUuid: paymentId, amountInCents, shortDescription: tenderPayment?.shortDescription }))
      } else {
        dispatch(addSignatureToOrder(orderUuid, signature))
        dispatch(addTipToOrder(orderUuid, tipInCents))
        dispatch(syncOrderInitiated(orderUuid))
        dispatch(syncOrderRequested(orderUuid))
      }
    },
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Signature))
